import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getIntegrationByType } from '@/components/organisms/Integrations';
import { useAllConnections } from '@/components/pages/ActivityLogs/hooks/useAllConnections';
import { FilterEmptyState } from '@/components/ui/custom/FilterEmptyState';
import useDesks from '@/hooks/useDesks';
import { Desk } from '@/shared/types/desk';
import { Integration, IntegrationType } from '@/shared/types/integration';

import { FilterCheckboxItem } from './FilterCheckboxItem';
import { SearchableCheckboxList } from './SearchableCheckboxList';
import { FilterPanelProps } from './types';

export type IntegrationOption = { value: string; label: string; type?: IntegrationType };

/**
 * Labels each integration "Environment › Integration", since most are just named "web".
 * With environments picked, only their integrations are offered. Picked integrations are
 * always offered, even if since deleted, so they can still be unticked.
 */
export const buildIntegrationOptions = (
  integrations: Integration[],
  desks: Pick<Desk, 'desk_id' | 'name'>[],
  deskIds?: string[],
  selected: Array<{ value: string; label: string }> = []
): IntegrationOption[] => {
  const deskNames = new Map(desks.map((desk) => [desk.desk_id, desk.name]));
  const selectedIds = selected.map((item) => item.value);
  const options: IntegrationOption[] = integrations
    .filter(
      (integration) =>
        !deskIds?.length ||
        deskIds.includes(integration.desk_id) ||
        selectedIds.includes(integration.integration_id)
    )
    .map((integration) => ({
      value: integration.integration_id,
      label: `${deskNames.get(integration.desk_id) ?? integration.desk_id} › ${integration.name}`,
      type: integration.type,
    }));
  const known = new Set(integrations.map((integration) => integration.integration_id));
  return [...options, ...selected.filter((item) => !known.has(item.value))];
};

/**
 * Renders the account's integrations with checkboxes. Mounted only while the
 * filter panel is open, so the per-environment lists are fetched on demand.
 */
export const IntegrationsFilter = ({
  filterIndex,
  searchText = '',
  onSearchChange = () => {},
}: FilterPanelProps) => {
  const { t } = useTranslation();
  const { desks } = useDesks();
  const { connections, isLoading } = useAllConnections();
  const { control, getValues } = useFormContext();

  const { update } = useFieldArray({ control, name: 'filters' });

  const filters = useWatch({ control, name: 'filters' }) as
    Array<{ type?: string; deskIds?: string[] }> | undefined;
  const deskIds = filters?.find((filter) => filter?.type === 'deskIds')?.deskIds;
  const selected: IntegrationOption[] =
    useWatch({ control, name: `filters.${filterIndex}.integrationIds` }) || [];
  const selectedIds = selected.map((item) => item.value);

  const options = buildIntegrationOptions(connections, desks ?? [], deskIds, selected);

  const handleToggle = (option: IntegrationOption, checked: boolean) => {
    const next = checked
      ? [...selected, { value: option.value, label: option.label }]
      : selected.filter((item) => item.value !== option.value);

    const currentFilter = getValues(`filters.${filterIndex}`);
    update(filterIndex, { ...currentFilter, integrationIds: next });
  };

  if (isLoading) {
    return <FilterEmptyState text={t('common.loading')} />;
  }

  if (options.length === 0) {
    return <FilterEmptyState text={t('analytics.filters.no_integrations')} />;
  }

  return (
    <SearchableCheckboxList
      searchText={searchText}
      onSearchChange={onSearchChange}
      items={options}
      getSearchTerms={({ label }) => [label]}
      placeholder={t('analytics.filters.search_integrations')}
      renderItem={(option) => {
        const Icon = option.type ? getIntegrationByType(option.type)?.Icon : undefined;
        return (
          <FilterCheckboxItem
            key={option.value}
            id={`integration-${option.value}`}
            checked={selectedIds.includes(option.value)}
            onCheckedChange={(checked) => handleToggle(option, checked as boolean)}
          >
            <div className="flex min-w-0 items-center gap-2">
              {Icon ? <Icon size={16} /> : null}
              <span className="truncate">{option.label}</span>
            </div>
          </FilterCheckboxItem>
        );
      }}
    />
  );
};
